import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import useTranslation from 'next-translate/useTranslation';
import styles from './couponList.module.scss';
import {
  getCouponLink,
  getExpired,
  getCouponName,
  compareCoupon,
  isHeroCoupon,
  CouponDetails,
  getCouponById,
} from '../../helpers/coupons';
import { getCityNameFromLocation } from '../../helpers/location';
import cx from 'classnames';
import { Coupon, CouponRedemptionMethod } from '../../definitions/CouponDefinitions';
import useUserAgent from '../../hooks/useUserAgent';
import { ll } from '../../helpers/date';
import { icons } from '../../../images/Icons';
import { useRouter } from 'next/router';

Modal.setAppElement('#__next');

interface CouponListProps {
  coupons: Coupon[];
  category: string;
  couponData?: CouponDetails;
  location: string;
  shallowRouting?: boolean;
}

const MAX_COUPONS = 50;

export default function CouponList({
  coupons,
  category,
  couponData,
  location,
  shallowRouting = true,
}: CouponListProps): JSX.Element {
  const { t } = useTranslation('application');
  const router = useRouter();
  const { isGooglebot } = useUserAgent();
  const [heroCoupons, setHeroCoupons] = useState<Coupon[]>([]);
  const [restCoupons, setRestCoupons] = useState<Coupon[]>([]);
  const [couponsVisible, setCouponsVisible] = useState<number>(MAX_COUPONS);
  const [coupon, setCoupon] = useState<Coupon>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const locale = router.locale;

  useEffect(() => {
    if (coupons && coupons.length && couponData) {
      const c = getCouponById(couponData.id, coupons);
      if (c) {
        setCoupon(c);
        setIsModalOpen(true);
      }
    }
  }, [coupons]);

  useEffect(() => {
    if (coupon) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [coupon]);

  useEffect(() => {
    if (coupons && coupons.length > 0) {
      const hCoupons = [];
      const rCoupons = [];
      coupons
        .filter((coupon) => {
          return coupon.categories.includes(category);
        })
        .forEach((coupon) => {
          if (isHeroCoupon(coupon)) {
            hCoupons.push(coupon);
          } else {
            rCoupons.push(coupon);
          }
        });
      hCoupons.sort(compareCoupon);
      rCoupons.sort(compareCoupon);
      if (hCoupons.length > couponsVisible) {
        // Case 1: number of hero coupons exceeds what we can show. Only show the hero coupons that we can
        hCoupons.splice(0, couponsVisible);
        setHeroCoupons(hCoupons);
        setRestCoupons([]);
      } else {
        // Case 2: display a combination of hero and regular coupons
        const startIdx = couponsVisible - hCoupons.length;
        const numToDelete = rCoupons.length - startIdx;
        rCoupons.splice(startIdx, numToDelete);
        setHeroCoupons(hCoupons);
        setRestCoupons(rCoupons);
      }
    }
  }, [coupons, couponsVisible]);

  function handleShowMore() {
    setCouponsVisible(couponsVisible + MAX_COUPONS);
  }

  function handleOpenModal(id: number) {
    const c = getCouponById(id, coupons);
    if (c) {
      setCoupon(c);
      setIsModalOpen(true);
      const t = getCouponLink(c, location, locale);
      router.push(t, undefined, { shallow: true });
    }
  }

  function getAlt(coupon: Coupon) {
    const city = getCityNameFromLocation(location);
    return t('search_page_alt', {
      city,
      itemName: getCouponName(coupon),
    });
  }

  function getH1() {
    if (coupon) {
      const h1 = t('coupon_page_h1', {
        name: coupon.brand ? coupon.brand : coupon.getName(),
      });
      return <h1>{h1}</h1>;
    }
    return null;
  }

  function getH3(coupon: Coupon) {
    const status = getExpired(coupon) ? t('expired') : t('online');
    const itemName = getCouponName(coupon);
    return `${itemName} - ${status}`;
  }

  function renderCoupon(coupon: Coupon) {
    return (
      <div key={coupon.id} className={styles.couponClipping} onClick={() => handleOpenModal(coupon.id)}>
        <div key={coupon.id} className={styles.couponContainer}>
          <div className={styles.coupon}>
            <div className={styles.image}>
              <img alt={getAlt(coupon)} src={coupon.imageUrl} />
            </div>
            <div className={styles.info}>
              <h3>{getH3(coupon)}</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function handleCloseButtonClick() {
    setCoupon(null);
    router.push(`/${locale}/${location}/coupons`, undefined, { shallow: shallowRouting });
  }

  function renderDialogHeader() {
    return (
      <div className={styles.flippDialogHeader}>
        <header className={styles.flexHorizontal}>
          <div className={styles.title}>
            <div>{t('coupon_details_title')}</div>
          </div>
          <button
            className={styles.closeButton}
            title={t('close_dialog_button')}
            aria-label={t('close_dialog_button')}
            onClick={handleCloseButtonClick}
          >
            {icons.searchClose}
          </button>
        </header>
      </div>
    );
  }

  function renderCouponDialog() {
    return (
      <div style={{ height: '100%' }}>
        <div className={styles.content}>
          {renderDialogHeader()}
          <div className={styles.flippToastContainer}>
            <div style={{ height: '100%' }}>
              <div className={styles.inner}>
                <div className={styles.image}>
                  <img alt="" src={coupon.imageUrl} />
                </div>
                <div className={styles.info}>
                  <div className={cx(styles.tags, styles.flexHorizontal)}>
                    <div className={styles.type}>
                      {coupon.redemptionMethod === CouponRedemptionMethod.PRINT ? (
                        <img
                          className={styles.icon}
                          src="https://f.wishabi.net/creative/Distribution/images/icons/print-default5.svg"
                          alt=""
                        />
                      ) : coupon.redemptionMethod === CouponRedemptionMethod.SAVE_TO_CARD ? (
                        <img
                          className={styles.icon}
                          src="https://f.wishabi.net/creative/Distribution/images/icons/card.svg"
                          alt=""
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.couponSave}>
                    <p className={styles.save}>{t('save')}</p>
                    {coupon.dollarsOff !== null ? (
                      <div className={styles.dollars}>${coupon.dollarsOff.toFixed(2)}</div>
                    ) : coupon.percentOff !== null ? (
                      <p className={styles.percentOff}>{`${(coupon.percentOff * 100).toFixed(0)}%`}</p>
                    ) : coupon.saleStory ? (
                      <p className={styles.saleStory}>{coupon.saleStory}</p>
                    ) : null}
                  </div>
                  {isGooglebot ? getH1() : null}
                  <p className={styles.promotionText}>{coupon.promotionText}</p>
                  {coupon.validFrom || coupon.validTo ? (
                    <div className={styles.couponValidity}>
                      {t('flexible_validity', {
                        from: coupon.validFrom ? `from ${ll(coupon.validFrom)}` : '',
                        to: coupon.validTo ? `to ${ll(coupon.validTo)}` : '',
                      })}
                    </div>
                  ) : null}
                  {coupon.matchUps.length > 0 ? (
                    <section className={styles.matchups}>
                      <h2>{t('matching_coupons')}</h2>
                    </section>
                  ) : null}
                  {couponData ? <section className={styles.tnc}>{couponData.disclaimer_text}</section> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className={styles.couponsWrapper}>
          {heroCoupons &&
            heroCoupons.map((coupon: Coupon) => {
              return renderCoupon(coupon);
            })}
        </div>
        <div className={styles.couponsWrapper}>
          {restCoupons &&
            restCoupons.map((coupon: Coupon) => {
              return renderCoupon(coupon);
            })}
        </div>
        {coupons.length > MAX_COUPONS ? (
          <div className={styles.buttonArea}>
            <button
              className={cx({
                disabled: couponsVisible >= coupons.length,
              })}
              onClick={handleShowMore}
            >
              <span>{t('show_more')}</span>
            </button>
          </div>
        ) : null}
      </div>
      {coupon && (
        <Modal
          isOpen={isModalOpen}
          contentLabel="Post modal"
          // styles={customStyles}
          className={styles.couponPop}
          overlayClassName={styles.overlay}
        >
          {renderCouponDialog()}
        </Modal>
      )}
    </>
  );
}
